import BankLogo from "@/components/molecules/BankLogo";
import { GlobalHeaderDropDown } from "@/components/molecules/GlobalHeaderDropDown";
import {
  HelpDropDown,
  HelpDropDownItems,
} from "@/components/organisms/HelpDropDown";
import { useIsSpMode } from "@/hooks";
import config from "@/libs/config";
import { color } from "@/styles/theme";
import { AuthStatus } from "@/contexts/AuthContext";
import { Header } from "@moneyforward/mfx_biz_react_components";
import { useRouter } from "next/router";
import useFixedAuth from "@/libs/useFixedAuth";
import logoImage from "@/assets/logo.png";
import {
  AccountDropDown,
  AccountDropDownItems,
} from "@/components/organisms/AccountDropDown";
import { urlWithQuery } from "@/libs/url";
import { ReactNode } from "react";

type Props = {
  term?: boolean;
  disableAccountDropDown?: boolean;
  disableLogoLink?: boolean;
};

// A note on logo sizing.
//
// The Mikatano ID logo is 16px tall, but the bank logo is 24px tall.
//
// This is deliberate as the ID logo has no margins (aka full-bleed), while the
// bank logos often have a small margin.
//
// Further, most bank logo adopt this design pattern of a large logo on the left
// that uses all the available vertical space, followed by text on the right that
// has considerable vertical spacing.
//
// Due to the above, if we make the Mikatano ID logo and the bank logo the same
// size, an optical illusion is created where the Mikatano ID logo appears much
// larger than the bank logo.

export const GlobalHeader = (props: Props) => {
  const router = useRouter();
  const { app } = router.query;

  const { authStatus } = useFixedAuth();

  const isSpMode = useIsSpMode();
  const isSignedIn = authStatus === AuthStatus.SignedIn;

  const showAccountDropDown =
    !props.term && isSignedIn && !props.disableAccountDropDown;

  const logoSrc = `/logo/${config.SUBDOMAIN}_logo.png`;

  const HomeA = ({ children }: { children: ReactNode }) => {
    const homeHref = props.disableLogoLink ? "#" : urlWithQuery("/", app);
    return (
      <a href={homeHref} css={{ display: "flex", justifyItems: "center" }}>
        {children}
      </a>
    );
  };

  return (
    <Header css={{ zIndex: 3 }}>
      {isSpMode && (
        <HomeA>
          <BankLogo src={logoSrc} />
        </HomeA>
      )}
      <Header.CommonNavigation
        css={{
          borderBottom: `1px solid ${color.gray300}`,
        }}
      >
        <Header.LeftSection>
          {!isSpMode && (
            <>
              <HomeA>
                <BankLogo src={logoSrc} />
              </HomeA>
              <Header.Divider />
            </>
          )}
          <HomeA>
            <Header.MikatanoLogo
              variant="xid"
              height={16}
              color={config.BANK_COLOR}
            />
          </HomeA>
        </Header.LeftSection>
        {!isSpMode && (
          <Header.RightSection
            css={{
              display: "flex",
              gap: 8,
            }}
          >
            <HelpDropDown />
            {showAccountDropDown && <AccountDropDown />}
          </Header.RightSection>
        )}
        {isSpMode && (
          <MobileDropDown showAccountDropDown={showAccountDropDown} />
        )}
      </Header.CommonNavigation>
    </Header>
  );
};

const MobileDropDown = ({
  showAccountDropDown,
}: {
  showAccountDropDown: boolean;
}) => {
  const { authStatus } = useFixedAuth();
  const isSignedIn = authStatus === AuthStatus.SignedIn;

  const icon = isSignedIn ? "manageAccounts" : "help";

  return (
    <GlobalHeaderDropDown>
      <GlobalHeaderDropDown.Header icon={icon} />

      <GlobalHeaderDropDown.Body width={200}>
        {isSignedIn && (
          <>
            {showAccountDropDown && <AccountDropDownItems />}
            <HelpDropDownItems showSeparator={false} />
            <GlobalHeaderDropDown.Separator />
            <GlobalHeaderDropDown.ItemSignOut />
          </>
        )}
        {!isSignedIn && <HelpDropDownItems showSeparator={false} />}
      </GlobalHeaderDropDown.Body>
    </GlobalHeaderDropDown>
  );
};
